import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Form, Row, Col, Alert, Button } from 'react-bootstrap';
import { TimelineEvent, TimelineEventType, getEventTypeLabel } from '../../types/timeline';
import { BuildingSummary, GroupTag } from '../../types/protocols';
import { ReportDraftFilters, ReportDraftLocationLevel, ReportDraftScope } from '../../types/reportDrafts';
import { eventMatchesFilters } from '../../utils/reportDraftView';
import { getBuildingTags, getLocationCategories, LocationCategoriesConfig } from '../../api/mapConfigApi';
import CustomMultiSelect from '../CustomMultiSelect';

interface TimelineFilterFormProps {
    allEvents: TimelineEvent[];
    buildingSummaries: Map<string, BuildingSummary>;
    filters: ReportDraftFilters;
    onChange: (filters: ReportDraftFilters) => void;
}

const LOCATION_LEVELS: ReportDraftLocationLevel[] = ['category1', 'category2', 'category3', 'category4', 'category5'];

/**
 * The one-time draft creation filter step: year range, event types (with an
 * explicit "Alla" choice), and the building scope — one of: alla byggnader, a
 * group tag, a location category value, or a handpicked building list. The
 * draft's membership is seeded from these filters and is fixed once created.
 *
 * The live count uses the same eventMatchesFilters predicate as seeding
 * (plan F1).
 */
const TimelineFilterForm: React.FC<TimelineFilterFormProps> = ({
    allEvents,
    buildingSummaries,
    filters,
    onChange
}) => {
    const [availableBuildingTags, setAvailableBuildingTags] = useState<GroupTag[]>([]);
    const [locationConfig, setLocationConfig] = useState<LocationCategoriesConfig | null>(null);
    const [loadingConfig, setLoadingConfig] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            getBuildingTags().catch(err => {
                console.error('Failed to load building tags:', err);
                return [] as GroupTag[];
            }),
            getLocationCategories()
        ]).then(([tags, locations]) => {
            if (cancelled) return;
            setAvailableBuildingTags(tags);
            setLocationConfig(locations);
        }).finally(() => {
            if (!cancelled) setLoadingConfig(false);
        });
        return () => { cancelled = true; };
    }, []);

    const availableYears = useMemo(() => {
        const years = new Set<number>();
        allEvents.forEach(event => years.add(event.date.getFullYear()));
        return Array.from(years).sort((a, b) => a - b);
    }, [allEvents]);

    const allBuildings = useMemo(() => {
        const buildings: Array<{ key: string; name: string }> = [];
        buildingSummaries.forEach((summary, key) => {
            buildings.push({ key, name: summary.name || 'Namnlös byggnad' });
        });
        return buildings.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [buildingSummaries]);

    /** Location levels that actually have configured values. */
    const availableLocationLevels = useMemo(() => {
        if (!locationConfig) return [] as { level: ReportDraftLocationLevel; label: string }[];
        return LOCATION_LEVELS
            .filter(level => locationConfig[level] && Object.keys(locationConfig[level]!).length > 0)
            .map(level => ({ level, label: locationConfig.labels?.[level] || level }));
    }, [locationConfig]);

    const locationOptionsForLevel = useCallback((level: ReportDraftLocationLevel | undefined) => {
        if (!level || !locationConfig?.[level]) return [] as { key: string; name: string }[];
        return Object.entries(locationConfig[level]!)
            .map(([key, value]) => ({ key, name: value.name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [locationConfig]);

    const matchCount = useMemo(
        () => allEvents.filter(event => eventMatchesFilters(event, filters)).length,
        [allEvents, filters]
    );

    const eventTypeOptions = useMemo(() =>
        Object.values(TimelineEventType).map(type => ({
            label: getEventTypeLabel(type),
            value: type
        })),
    []);

    const setScope = useCallback((scope: ReportDraftScope) => {
        onChange({ ...filters, scope });
    }, [filters, onChange]);

    const handleBuildingToggle = useCallback((buildingKey: string) => {
        const current = new Set(filters.scope.buildingKeys || []);
        if (current.has(buildingKey)) {
            current.delete(buildingKey);
        } else {
            current.add(buildingKey);
        }
        setScope({ type: 'buildings', buildingKeys: Array.from(current) });
    }, [filters.scope.buildingKeys, setScope]);

    const scopeType = filters.scope.type;

    return (
        <div>
            {/* Year Selection */}
            <div className="mb-4">
                <h6 className="mb-2">Tidsperiod</h6>
                <Row>
                    <Col xs={6}>
                        <Form.Group>
                            <Form.Label className="small text-muted">Från år</Form.Label>
                            <Form.Select
                                value={filters.startYear}
                                onChange={(e) => {
                                    const newStart = parseInt(e.target.value);
                                    onChange({
                                        ...filters,
                                        startYear: newStart,
                                        endYear: Math.max(newStart, filters.endYear)
                                    });
                                }}
                            >
                                {availableYears.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={6}>
                        <Form.Group>
                            <Form.Label className="small text-muted">Till år</Form.Label>
                            <Form.Select
                                value={filters.endYear}
                                onChange={(e) => {
                                    const newEnd = parseInt(e.target.value);
                                    onChange({
                                        ...filters,
                                        endYear: newEnd,
                                        startYear: Math.min(newEnd, filters.startYear)
                                    });
                                }}
                            >
                                {availableYears.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            {/* Event types */}
            <div className="mb-4">
                <h6 className="mb-2">Händelsetyper</h6>
                <Form.Check
                    type="switch"
                    id="alla-eventTypes"
                    label="Alla händelsetyper"
                    checked={filters.eventTypes === undefined}
                    onChange={(e) => onChange({
                        ...filters,
                        eventTypes: e.target.checked ? undefined : Object.values(TimelineEventType)
                    })}
                    className="mb-1"
                />
                {filters.eventTypes !== undefined && (
                    <CustomMultiSelect
                        label="Händelsetyper"
                        options={eventTypeOptions}
                        value={filters.eventTypes}
                        onChange={(value) => onChange({ ...filters, eventTypes: value as TimelineEventType[] })}
                        placeholder="Välj händelsetyper"
                        selectedItemsLabel="{0} händelsetyper"
                        showFilterBadge
                    />
                )}
            </div>

            {/* Building scope */}
            <div className="mb-4">
                <h6 className="mb-2">Byggnader</h6>
                <Form.Check
                    type="radio"
                    name="draft-scope"
                    id="scope-all"
                    label="Alla byggnader"
                    checked={scopeType === 'all'}
                    onChange={() => setScope({ type: 'all' })}
                    className="mb-1"
                />
                <Form.Check
                    type="radio"
                    name="draft-scope"
                    id="scope-tag"
                    label="Byggnader med en grupptagg"
                    checked={scopeType === 'tag'}
                    disabled={availableBuildingTags.length === 0}
                    onChange={() => setScope({ type: 'tag', tagKey: availableBuildingTags[0]?.key })}
                    className="mb-1"
                />
                {scopeType === 'tag' && (
                    <Form.Select
                        className="mb-2 ms-4"
                        style={{ maxWidth: '320px' }}
                        value={filters.scope.tagKey || ''}
                        onChange={(e) => setScope({ type: 'tag', tagKey: e.target.value })}
                    >
                        {availableBuildingTags.map(tag => (
                            <option key={tag.key} value={tag.key}>{tag.tag}</option>
                        ))}
                    </Form.Select>
                )}
                <Form.Check
                    type="radio"
                    name="draft-scope"
                    id="scope-location"
                    label="Byggnader på en plats"
                    checked={scopeType === 'location'}
                    disabled={loadingConfig || availableLocationLevels.length === 0}
                    onChange={() => {
                        const level = availableLocationLevels[0]?.level;
                        setScope({
                            type: 'location',
                            locationLevel: level,
                            locationKey: locationOptionsForLevel(level)[0]?.key
                        });
                    }}
                    className="mb-1"
                />
                {scopeType === 'location' && (
                    <Row className="mb-2 ms-3" style={{ maxWidth: '560px' }}>
                        <Col xs={6}>
                            <Form.Select
                                value={filters.scope.locationLevel || ''}
                                onChange={(e) => {
                                    const level = e.target.value as ReportDraftLocationLevel;
                                    setScope({
                                        type: 'location',
                                        locationLevel: level,
                                        locationKey: locationOptionsForLevel(level)[0]?.key
                                    });
                                }}
                            >
                                {availableLocationLevels.map(({ level, label }) => (
                                    <option key={level} value={level}>{label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Select
                                value={filters.scope.locationKey || ''}
                                onChange={(e) => setScope({
                                    type: 'location',
                                    locationLevel: filters.scope.locationLevel,
                                    locationKey: e.target.value
                                })}
                            >
                                {locationOptionsForLevel(filters.scope.locationLevel).map(option => (
                                    <option key={option.key} value={option.key}>{option.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                )}
                <Form.Check
                    type="radio"
                    name="draft-scope"
                    id="scope-buildings"
                    label="Valda byggnader"
                    checked={scopeType === 'buildings'}
                    onChange={() => setScope({ type: 'buildings', buildingKeys: [] })}
                    className="mb-1"
                />
                {scopeType === 'buildings' && (
                    <div className="ms-4">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small text-muted">
                                {(filters.scope.buildingKeys || []).length} av {allBuildings.length} valda
                            </span>
                            <div>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 me-3"
                                    onClick={() => setScope({ type: 'buildings', buildingKeys: allBuildings.map(b => b.key) })}
                                >
                                    Välj alla
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    onClick={() => setScope({ type: 'buildings', buildingKeys: [] })}
                                >
                                    Avmarkera alla
                                </Button>
                            </div>
                        </div>
                        <div
                            style={{
                                maxHeight: '150px',
                                overflowY: 'auto',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '8px'
                            }}
                        >
                            {allBuildings.map(building => (
                                <Form.Check
                                    key={building.key}
                                    type="checkbox"
                                    id={`building-${building.key}`}
                                    label={building.name}
                                    checked={(filters.scope.buildingKeys || []).includes(building.key)}
                                    onChange={() => handleBuildingToggle(building.key)}
                                    className="mb-1"
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Match Count */}
            <Alert variant="info" className="mb-0">
                <strong>{matchCount}</strong> {matchCount === 1 ? 'händelse' : 'händelser'} matchar filtren
            </Alert>
        </div>
    );
};

export default TimelineFilterForm;
